//#region IMPORTS
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import {
  Store,
  StoreState,
  StoreParams,
  StoreRequest,
  StoreFilters,
} from "../../models/store";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INIT
// Set initial state
const initialExtraState: StoreState = {
  filters_loaded: false,
  stores_loaded: false,
  store_params: initParams(),
  metaData: undefined,
  filters: initFilters(),
  loading: false,
  status: "idle",
};

// Set initial query parameters
function initParams(): StoreParams {
  return {
    page: 1,
    limit: 25,
  };
}

// Set all filter options
function initFilters(): StoreFilters {
  return {
    language: [],
    country_name: [],
    city: [],
  };
}
//#endregion

//#region CREATE ADPATER
const storesAdapter = createEntityAdapter<Store>();
//#endregion

//#region READ
// Fetch All
export const fetchStoresAsync = createAsyncThunk<
  Store[],
  void,
  { state: RootState }
>("stores/fetchStoresAsync", async (_, thunkAPI) => {
  const storeParams = getAxiosParams(thunkAPI.getState().stores.store_params);
  try {
    const response = await agent.StoreManagement.list(storeParams);
    thunkAPI.dispatch(setStoresMetaData(response.meta));
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch all filters
export const fetchFilters = createAsyncThunk(
  "stores/fetchFiltersAsync",
  async (_, thunkAPI) => {
    try {
      return agent.StoreManagement.fetchFilters();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  }
);
//#endregion

//#region CREATE
export const createStoreAsync = createAsyncThunk<
  Store,
  { values: StoreRequest }
>("stores/createStoreAsync", async ({ values }, thunkAPI) => {
  try {
    const { data } = await agent.StoreManagement.create({ ...values });
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region UPDATE
export const updateStoreAsync = createAsyncThunk<
  Store,
  { id: number; values: StoreRequest }
>("stores/updateStoreAsync", async ({ id, values }, thunkAPI) => {
  try {
    const { data } = await agent.StoreManagement.update(id, values);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region DELETE
export const deleteStoreAsync = createAsyncThunk<number, number>(
  "stores/deleteStoreAsync",
  async (id, thunkAPI) => {
    try {
      await agent.StoreManagement.delete(id);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  }
);
//#endregion

//#region CREATE REDUCER SLICE
export const storesSlice = createSlice({
  name: "stores",
  initialState: storesAdapter.getInitialState<StoreState>(initialExtraState),
  reducers: {
    setStoreParams: (state, action) => {
      state.stores_loaded = false;
      state.store_params = {
        ...state.store_params,
        ...action.payload,
        page: 1,
      };
    },
    clearStoreParams: (state) => {
      state.stores_loaded = false;
      state.store_params = initParams();
    },
    clearStoreParam: (state, action) => {
      const newParmams = { ...state.store_params };
      delete newParmams[action.payload];
      state.stores_loaded = false;
      state.store_params = newParmams;
    },
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters: (state) => {
      state.filters = initFilters();
    },
    setStoresPageNumber: (state, action) => {
      state.stores_loaded = false;
      state.loading = true;
      state.store_params = { ...state.store_params, ...action.payload };
    },
    setStoresMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetStoreParams: (state) => {
      state.stores_loaded = false;
      state.store_params = initParams();
    },
    clearStores: () =>
      storesAdapter.getInitialState<StoreState>(initialExtraState),
    setStore: (state, action) => storesAdapter.upsertOne(state, action.payload),
    removeStore: (state, action) =>
      storesAdapter.removeOne(state, action.payload),
  },
  extraReducers: (builder) => {
    // FETCH stores
    builder.addCase(fetchStoresAsync.pending, (state) => {
      state.status = "pendingFetchStores";
      state.loading = true;
    });
    builder.addCase(fetchStoresAsync.fulfilled, (state, action) => {
      storesAdapter.setAll(state, action.payload);
      state.status = "idle";
      state.stores_loaded = true;
      state.loading = false;
    });
    builder.addCase(fetchStoresAsync.rejected, (state, action) => {
      state.status = "idle";
      state.loading = false;
      toast.error(`${action.payload || "Unable to fetch stores"}`);
    });

    // FETCH filters
    builder.addCase(fetchFilters.pending, (state) => {
      state.status = "pendingFetchFilters";
    });
    builder.addCase(fetchFilters.fulfilled, (state, action) => {
      state.filters.city = action.payload.cities ?? [];
      state.filters.language = action.payload.languages ?? [];
      state.filters.country_name = action.payload.countries.map(
        (country: { name: string }) => country.name
      );
      state.filters_loaded = true;
      state.status = "idle";
    });
    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.status = "idle";
      toast.error(`${action.payload || "Unable to fetch store filtering"}`);
    });

    // CREATE store
    builder.addCase(createStoreAsync.pending, (state) => {
      state.status = "PendingCreateStore";
      state.loading = true;
    });
    builder.addCase(createStoreAsync.fulfilled, (state, action) => {
      storesAdapter.addOne(state, action.payload);
      toast.success(`${action.payload.store_name} created.`);
      state.stores_loaded = false;
      state.status = "idle";
      state.loading = false;
    });
    builder.addCase(createStoreAsync.rejected, (state) => {
      state.status = "idle";
      toast.error("Something went realy wrong");
    });

    // UPDATE store
    builder.addCase(updateStoreAsync.pending, (state, action) => {
      state.status = "PendingUpdateStore" + action.meta.arg.id;
    });
    builder.addCase(updateStoreAsync.fulfilled, (state, action) => {
      storesAdapter.upsertOne(state, action.payload);
      state.status = "idle";
    });
    builder.addCase(updateStoreAsync.rejected, (state) => {
      state.status = "idle";
      toast.error("Something went realy wrong");
    });

    //#region DELETE store
    builder.addCase(deleteStoreAsync.pending, (state) => {
      state.status = "PendingDeleteStore";
      state.loading = true;
    });
    builder.addCase(deleteStoreAsync.fulfilled, (state, action) => {
      storesAdapter.removeOne(state, action.payload);
      toast.success("Store deleted.");
      state.status = "idle";
      state.loading = false;
    });
    builder.addCase(deleteStoreAsync.rejected, (state) => {
      state.status = "idle";
      toast.error("Something went realy wrong");
    });
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const storeSelectors = storesAdapter.getSelectors(
  (state: RootState) => state.stores
);
//#endregion

//#region EXPORT
export const {
  setStoresPageNumber,
  setStoresMetaData,
  resetStoreParams,
  clearStoreParams,
  clearStoreParam,
  setStoreParams,
  updateFilters,
  resetFilters,
  removeStore,
  clearStores,
  setStore,
} = storesSlice.actions;
//#endregion
